/*
  Site tests: persistence and measurement plane.
  Companion to snippets/site-tests-head.liquid, which decides every arm before paint
  and exposes window.SIAB_TESTS = { vid, tests: { <key>: { id, arm, inScope, ... } } }.

  This file:
    1. Records exposure. A visitor is exposed to a test the first time they load one
       of its pages, or, for a test with no pages, when page code calls
       SIAB_TESTS.expose('<key>') (e.g. the homepage hero button, WW-202610-hp-shopnow). Exposed test ids persist in localStorage so a later cart on any
       page still carries them.
    2. Stamps the exposed tests onto the Shopify cart as attributes so they ride onto
       the order (policy shirt-in-a-box-bucket-onsite-experiments-via-cart-attribute):
         _site_tests = "WW-202610-shop-filters:B" (semicolon-separated when several;
                       a QA-forced arm reads "WW-202610-shop-filters:B:forced")
         _site_vid   = the visitor id
       Stamps on page load when the cart has items, and after any /cart/add call.
       /cart/update.js merges attributes, so this never touches the offer test's
       _offer_test / _offer_vid keys.
    3. Emits site_test_exposure once per session per test to PostHog (diagnostics)
       and Converge (exposure denominator), and registers the arms as PostHog super
       properties so every later event carries them.

  Never decides anything. Shopify orders are the truth; PostHog is diagnosis only.
*/
(function (w, d) {
  'use strict';
  var S = w.SIAB_TESTS;
  if (!S || !S.vid || !S.tests) return;

  var STORE = 'siab_tests_exposed';
  function readExposed() {
    try { return JSON.parse(w.localStorage.getItem(STORE) || '{}') || {}; } catch (e) { return {}; }
  }
  function writeExposed(x) {
    try { w.localStorage.setItem(STORE, JSON.stringify(x)); } catch (e) { console.warn('[site-tests] localStorage unavailable', e); }
  }

  function track(name, props) {
    try { if (w.posthog && typeof w.posthog.capture === 'function') w.posthog.capture(name, props); } catch (e) { console.warn('[site-tests] posthog', e); }
    try { if (typeof w.cvg === 'function') w.cvg({ method: 'track', eventName: name, properties: props }); } catch (e) { console.warn('[site-tests] converge', e); }
  }

  // ---------- exposure ----------
  var exposed = readExposed();
  function stampOf(x) {
    return Object.keys(x).sort().map(function (id) { return id + ':' + x[id]; }).join(';');
  }
  var stampValue = '';
  function refreshStamp() {
    stampValue = stampOf(exposed);
    if (!stampValue) return;
    try {
      if (w.posthog && typeof w.posthog.register === 'function') w.posthog.register({ site_tests: stampValue, site_vid: S.vid });
    } catch (e) { /* posthog stub not ready; exposure events still carry the props */ }
  }
  function exposeTest(t, trigger) {
    // A forced QA arm is stamped as "A:forced" so staff orders can be dropped from the read.
    exposed[t.id] = t.forced ? t.arm + ':forced' : t.arm;
    var sk = 'siab_site_test_exposed_' + t.id;
    var seen = false;
    try { seen = !!w.sessionStorage.getItem(sk); w.sessionStorage.setItem(sk, '1'); } catch (e) { seen = false; }
    if (!seen) {
      track('site_test_exposure', {
        site_test: t.id, site_arm: t.arm, site_vid: S.vid, site_bucket: t.bucket,
        site_forced: t.forced, site_new_visitor: S.newVisitor, path: location.pathname, trigger: trigger
      });
    }
  }
  var keys = Object.keys(S.tests);
  for (var i = 0; i < keys.length; i++) {
    var t = S.tests[keys[i]];
    if (!t.inTest || !t.inScope) continue;
    exposeTest(t, 'page');
  }
  writeExposed(exposed);
  refreshStamp();

  // Manual exposure for tests with no paths (exposed by an action, not a page view).
  // Writes localStorage synchronously, so a caller may navigate straight after.
  S.expose = function (key) {
    var t = S.tests[key];
    if (!t || !t.inTest) return null;
    exposeTest(t, 'action');
    writeExposed(exposed);
    refreshStamp();
    return t.arm;
  };

  // ---------- cart stamping ----------
  var ATTR_TESTS = '_site_tests', ATTR_VID = '_site_vid';
  var stamping = false;
  // The latest stamp as a promise. Other cart code (the cart second-box offer) waits on it before
  // writing to the cart: Shopify keeps the last of two overlapping cart writes, so a stamp racing a
  // line change can undo that change.
  var stampP = Promise.resolve();
  S.whenStamped = function () { return stampP; };
  function stampCart(reason) {
    if (stamping) return stampP;
    stamping = true;
    stampP = fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!stampValue || !cart || !cart.item_count) return null;
        var a = cart.attributes || {};
        if (a[ATTR_TESTS] === stampValue && a[ATTR_VID] === S.vid) return null;
        var attrs = {}; attrs[ATTR_TESTS] = stampValue; attrs[ATTR_VID] = S.vid;
        return fetch('/cart/update.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        }).then(function (r) {
          if (!r.ok) throw new Error('cart/update ' + r.status);
          track('site_test_cart_stamped', { site_tests: stampValue, site_vid: S.vid, reason: reason });
        });
      })
      .catch(function (e) { console.warn('[site-tests] stamp failed', reason, e); })
      .then(function () { stamping = false; });
    return stampP;
  }

  var addRe = /\/cart\/add(\.js)?(\?|$)/;
  var _fetch = w.fetch;
  if (_fetch) {
    w.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = _fetch.apply(this, arguments);
      if (addRe.test(url)) p.then(function (r) { if (r && r.ok) setTimeout(function () { stampCart('fetch-add'); }, 120); }).catch(function () {});
      return p;
    };
  }
  var _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (typeof url === 'string' && addRe.test(url)) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) setTimeout(function () { stampCart('xhr-add'); }, 120);
      });
    }
    return _open.apply(this, arguments);
  };

  // Covers native form posts to /cart/add, which land on /cart with no JS hook.
  stampCart('page-load');
})(window, document);
